import { html, nothing, type TemplateResult } from "lit";
import { icons } from "../icons.ts";
import { ref } from "lit/directives/ref.js";
import type { GatewayAgentRow, SessionsListResult } from "../types.ts";
import { parseAgentSessionKey } from "../../../../src/sessions/session-key-utils.js";

export type MentionState = { open: boolean; query: string };

/** Same character class accepted by the backend `@token` parser (delegate-router). */
// JAISIU-3588 (followup) — find the LAST unterminated
// @token in the draft (not just at the start). This lets the
// `+` popover's Mention entry append `@` at the end of the
// existing text and have the picker open against the new
// token.
const MENTION_RE = /(^|\s)@([A-Za-z0-9._-]*)$/;

/**
 * Returns mention picker state derived from the current draft.
 *
 * The picker opens only when the entire draft begins with `@` and the mention
 * token has not been terminated by whitespace yet. This mirrors the backend's
 * `parseMessageForSubAgent`, which requires the mention at the start of the
 * message: anything else cannot delegate, so showing the picker would mislead.
 */
export function extractMentionQuery(draft: string): MentionState {
  // JAISIU-3588 (followup) — pick the LAST @token in the draft
  // so mid-message mentions (added by the `+` popover) work.
  const match = MENTION_RE.exec(draft);
  if (!match) {
    return { open: false, query: "" };
  }
  return { open: true, query: match[2] ?? "" };
}

export function filterAgents(agents: GatewayAgentRow[], query: string): GatewayAgentRow[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return agents.slice();
  }
  return agents.filter((agent) => {
    if (agent.id.toLowerCase().includes(q)) {
      return true;
    }
    const name = agent.name?.toLowerCase();
    return Boolean(name && name.includes(q));
  });
}

/**
 * "Active" = at least one persisted session targets this agent. Derived from
 * `sessions.list`, which is already fetched by the chat view, so no extra RPCs.
 */
export function agentHasRecentActivity(
  agentId: string,
  sessions: SessionsListResult | null,
): boolean {
  if (!sessions) {
    return false;
  }
  for (const session of sessions.sessions) {
    const parsed = parseAgentSessionKey(session.key);
    if (parsed?.agentId === agentId) {
      return true;
    }
  }
  return false;
}

export type MentionPickerProps = {
  agents: GatewayAgentRow[];
  sessions: SessionsListResult | null;
  query: string;
  activeIndex: number;
  onSelect: (agentId: string) => void;
  onHoverIndex: (index: number) => void;
  /** Close the picker (e.g. via the X button). Host removes the
   *  triggering `@` token from the draft. */
  onClose?: () => void;
};

export function renderMentionPicker(props: MentionPickerProps) {
  const matches = filterAgents(props.agents, props.query);
  if (matches.length === 0) {
    return nothing;
  }
  const effectiveIndex = Math.max(0, Math.min(props.activeIndex, matches.length - 1));
  return html`
    <div class="chat-mention-wrap">
      <div class="chat-mention-header">
        <span class="chat-mention-header__title">Mention an agent</span>
        ${props.onClose
          ? html`<button
              type="button"
              class="chat-mention-header__close"
              title="Close mention picker"
              aria-label="Close mention picker"
              @click=${() => props.onClose?.()}
            >
              ${icons.x}
            </button>`
          : nothing}
      </div>
      <div class="chat-mention" role="listbox" aria-label="Agents">
        ${matches.map((agent, index) => {
        const active = index === effectiveIndex;
        const label = agent.name?.trim() || agent.id;
        const live = agentHasRecentActivity(agent.id, props.sessions);
        return html`
          <button
            type="button"
            class="chat-mention__row ${active ? "active" : ""}"
            role="option"
            aria-selected=${active}
            ${ref((el) => {
              if (active && el) {
                (el as HTMLElement).scrollIntoView({
                  block: "nearest",
                  inline: "nearest",
                });
              }
            })}
            @mousedown=${(e: Event) => e.preventDefault()}
            @mouseenter=${() => props.onHoverIndex(index)}
            @click=${() => props.onSelect(agent.id)}
          >
            <span
              class="chat-mention__dot ${live ? "live" : "idle"}"
              title=${live ? "Recent session activity" : "No recent session"}
            ></span>
            <span class="chat-mention__label">${label}</span>
            <span class="chat-mention__id mono">${agent.id}</span>
          </button>
        `;
      })}
      </div>
    </div>
  `;
}
